import { api } from 'API'
import {
  InvitationPreflight,
  InvitationAcceptResult,
  CreateInvitationPayload,
  OrgInvitationRecord,
  LookupInviteCandidateResult
} from 'types/invitation'

/**
 * Org-scoped invite endpoints resolve the backend org context from the
 * `?orgId=` query param (see API org-context provider), NOT the URL path
 * `{orgId}`. The global axios interceptor auto-stamps `orgId` from the
 * *active* org in localStorage — which can differ from the team the invite UI
 * is acting on (the team-filter chip). Always send orgId = the explicit orgId
 * so the chosen team wins over the global stamp; otherwise search/create hit
 * the wrong org (e.g. a player already in team A looks "in-team" when inviting
 * into team B, so the candidate is wrongly hidden).
 */
const teamScope = (orgId: number) => ({ orgId: orgId })

/** GET /invitations/:token — public preflight (no auth) */
export async function validateInvitation(token: string): Promise<InvitationPreflight> {
  const response = await api.get(`/invitations/${token}`)
  return response.data as InvitationPreflight
}

/** POST /invitations/:token/accept — requires auth */
export async function acceptInvitation(token: string): Promise<InvitationAcceptResult> {
  const response = await api.post(`/invitations/${token}/accept`)
  return response.data as InvitationAcceptResult
}

/** POST /invitations/:token/decline — requires auth */
export async function declineInvitation(token: string): Promise<void> {
  await api.post(`/invitations/${token}/decline`)
}

export interface PendingProfileInvite {
  id: number
  orgId: number
  orgName: string | null
  intendedProfileType: 'player' | 'coach' | 'youth'
  token: string
  expiresAt: string
}

/**
 * GET /invitations/my-pending-profile-invites — auth required.
 * Cross-org list of PENDING invitations for the caller that still need a
 * profile (target FK is null on the server side). Used by SelectProfilePage
 * to show the "Create a {Type} profile to view your invitation" hint when
 * the user lands without an `?invitation=TOKEN` URL param.
 */
export async function getMyPendingProfileInvites(): Promise<PendingProfileInvite[]> {
  const response = await api.get('/invitations/my-pending-profile-invites')
  return response.data as PendingProfileInvite[]
}

/** POST /organizations/:orgId/invitations — admin/coach only */
export async function createInvitation(
  orgId: number,
  payload: CreateInvitationPayload
): Promise<OrgInvitationRecord> {
  const response = await api.post(`/organizations/${orgId}/invitations`, payload, {
    params: teamScope(orgId)
  })
  return response.data
}

export interface BulkCreateInvitationsResult {
  created: number
  message: string
  failed: Array<{
    inviteEmail: string
    targetUserId?: number
    reason: string
  }>
}

/** POST /organizations/:orgId/invitations/bulk — admin/coach only */
export async function bulkCreateInvitations(
  orgId: number,
  items: CreateInvitationPayload[]
): Promise<BulkCreateInvitationsResult> {
  const response = await api.post(
    `/organizations/${orgId}/invitations/bulk`,
    { items },
    { params: teamScope(orgId) }
  )
  return response.data
}

/** Item shape for the unified profile-aware bulk-invite endpoint. */
export interface BulkProfileInviteItem {
  inviteEmail: string
  intendedProfileType: 'player' | 'coach' | 'youth'
  targetProfileId?: number
  message?: string
}

/**
 * POST /organizations/:orgId/invitations/bulk — profile-aware variant.
 * Server runs invites in parallel via Promise.allSettled and returns
 * { created, failed } per item. Throws 400 ONLY when zero items succeed.
 * FE renders per-item toasts from the response.
 */
export async function bulkCreateProfileInvites(
  orgId: number,
  items: BulkProfileInviteItem[]
): Promise<BulkCreateInvitationsResult> {
  const response = await api.post(
    `/organizations/${orgId}/invitations/bulk`,
    { items },
    { params: teamScope(orgId) }
  )
  return response.data as BulkCreateInvitationsResult
}

/** POST /organizations/:orgId/invitations/:id/resend — admin/coach only */
export async function resendInvitation(
  orgId: number,
  invitationId: number
): Promise<OrgInvitationRecord> {
  const response = await api.post(
    `/organizations/${orgId}/invitations/${invitationId}/resend`,
    undefined,
    { params: teamScope(orgId) }
  )
  return response.data
}

/** GET /organizations/:orgId/invitations?status= — admin/coach only */
export async function listInvitations(
  orgId: number,
  status?: 'pending' | 'accepted' | 'declined' | 'expired' | 'revoked'
): Promise<OrgInvitationRecord[]> {
  const response = await api.get(
    `/organizations/${orgId}/invitations`,
    { params: { ...teamScope(orgId), ...(status ? { status } : {}) } }
  )
  return response.data
}

/** Sort keys accepted by the paginated invitation list. */
export type InvitationListSortBy = 'lastUpdated' | 'sentDate' | 'emailAsc' | 'emailDesc'

export interface IInvitationListParams {
  pageIndex?: number
  pageSize?: number
  /** Matches the invited email or the invitee's name. */
  keyword?: string
  statuses?: string[]
  /** `intendedProfileType` values: player / coach / youth. */
  profileTypes?: string[]
  /** Inviter tokens from a row's `invitedByToken` (`coach:12` / `athlete:7`). */
  invitedBy?: string[]
  sentWithinDays?: number
  sortBy?: InvitationListSortBy
}

export interface IInvitationListResult {
  results: OrgInvitationRecord[]
  /** Total matching the filters, not the page length — drives the pager. */
  totalCount: number
}

/**
 * GET /organizations/:orgId/invitations/list — admin only.
 *
 * The paginated, filtered list behind the Org Membership → Invitation table. The
 * server owns filtering, sorting and paging; array params go as CSV, matching
 * how /users/list takes its filters.
 */
export async function getInvitationList(
  orgId: number,
  params: IInvitationListParams
): Promise<IInvitationListResult> {
  const query: Record<string, string | number> = { ...teamScope(orgId) }
  if (params.pageIndex) query.pageIndex = params.pageIndex
  if (params.pageSize) query.pageSize = params.pageSize
  if (params.keyword?.trim()) query.keyword = params.keyword.trim()
  if (params.statuses?.length) query.statuses = params.statuses.join(',')
  if (params.profileTypes?.length) query.profileTypes = params.profileTypes.join(',')
  if (params.invitedBy?.length) query.invitedBy = params.invitedBy.join(',')
  if (params.sentWithinDays) query.sentWithinDays = params.sentWithinDays
  if (params.sortBy) query.sortBy = params.sortBy

  const response = await api.get(`/organizations/${orgId}/invitations/list`, { params: query })
  return response.data as IInvitationListResult
}

/** One selectable inviter for the Invited By filter. */
export interface InvitationInviterOption {
  /** Send back as `invitedBy`; collision-free across profile collections. */
  token: string
  name: string
}

/**
 * GET /organizations/:orgId/invitations/inviters — admin only.
 * Org-wide inviter options for the filter panel. Its own call because a paginated
 * list only ever sees one page's inviters.
 */
export async function getInvitationInviters(orgId: number): Promise<InvitationInviterOption[]> {
  const response = await api.get(`/organizations/${orgId}/invitations/inviters`, {
    params: teamScope(orgId)
  })
  return response.data as InvitationInviterOption[]
}

/** DELETE /organizations/:orgId/invitations/:id — admin/coach only */
export async function revokeInvitation(orgId: number, invitationId: number): Promise<void> {
  await api.delete(`/organizations/${orgId}/invitations/${invitationId}`, {
    params: teamScope(orgId)
  })
}

// Re-export so callers can import from 'API/invitation' without touching types directly.
export type { LookupInviteCandidateResult } from 'types/invitation'

/** Single row in the invite-search picker. Mirrors BE InviteSearchRow. */
export type InviteSearchRow =
  | {
      kind: 'profile'
      email: string
      userId: number
      profileType: 'player' | 'coach' | 'youth'
      targetProfileId: number
      firstName?: string
      lastName?: string
      displayName?: string
      /** Profile photo; FE falls back to initials when absent. */
      avatarUrl?: string
      /** True when a PENDING invitation already targets this profile in this team. */
      pendingInvitation?: boolean
    }
  | {
      kind: 'email-only'
      email: string
      userId: number
      pendingInvitation?: boolean
    }

export interface InviteSearchResult {
  results: InviteSearchRow[]
  /** Lowercased emails of users hidden because all their relevant profiles are in-team.
   *  FE uses this to suppress the H1 "Invite to join RallyPD" synth fallback. */
  hiddenInTeamEmails?: string[]
}

/**
 * GET /organizations/:orgId/invitations/search?q=&for= — admin/owner only.
 * Returns a flat list of pickable rows for the invite modal: one row per
 * non-in-team profile matching the intent, or an email-only row when the
 * matched account has no relevant profile.
 */
export async function searchInviteCandidates(
  orgId: number,
  q: string,
  forIntent: 'player' | 'coach'
): Promise<InviteSearchResult> {
  const response = await api.get(
    `/organizations/${orgId}/invitations/search`,
    { params: { ...teamScope(orgId), q, for: forIntent } }
  )
  return response.data as InviteSearchResult
}

/**
 * GET /organizations/:orgId/invitations/lookup?email= — new unified endpoint.
 * Returns profile-aware lookup: player/coach single-profile slots + youthProfiles array.
 * 403 if caller is not owner/admin; 400 if email missing.
 */
export async function lookupInviteCandidate({
  orgId,
  email
}: {
  orgId: number
  email: string
}): Promise<LookupInviteCandidateResult> {
  const response = await api.get(
    `/organizations/${orgId}/invitations/lookup`,
    { params: { ...teamScope(orgId), email } }
  )
  return response.data as LookupInviteCandidateResult
}

export interface CreateProfileInviteInput {
  orgId: number
  email: string
  intendedProfileType: 'player' | 'coach' | 'youth'
  /** Targets a specific existing profile. Omit for generic (account-missing) invites. */
  targetProfileId?: number
  message?: string
}

/**
 * POST /organizations/:orgId/invitations — invite a user with a specific intended profile type.
 * Requires OWNER or ADMIN role (enforced server-side via assertOwnerOrAdminRole).
 * Throws with response.status === 409 when a pending invite already exists for
 * the same (email, intendedProfileType) tuple.
 */
export async function createProfileInvite({
  orgId,
  email,
  intendedProfileType,
  targetProfileId,
  message
}: CreateProfileInviteInput): Promise<OrgInvitationRecord> {
  // BE controller schema expects `inviteEmail` (required) — do NOT rename to `email`
  // or the @requestBody schema rejects the body with "request body is invalid".
  const response = await api.post(
    `/organizations/${orgId}/invitations`,
    {
      inviteEmail: email,
      intendedProfileType,
      ...(targetProfileId !== undefined ? { targetProfileId } : {}),
      ...(message?.trim() ? { message: message.trim() } : {})
    },
    { params: teamScope(orgId) }
  )
  return response.data as OrgInvitationRecord
}
